import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, Sparkles, Wand2 } from "lucide-react";
import {
  api,
  type CarouselItem,
  type GraphicElement,
  type PostCompositionPlan,
  type PostInspirationBrief,
  type PostVerifyResult,
} from "@/lib/api";
import ElementSwapDrawer from "@/components/ElementSwapDrawer";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

type Props = {
  item: CarouselItem;
  onItem?: (item: CarouselItem) => void;
};

type StepId = 1 | 2 | 3 | 4;

const ROLE_LABELS: Record<string, string> = {
  underline: "Podkreślenie z biblioteki",
  underline_good: "Podkreślenie (dobrze)",
  hero_icon: "Ikona / ozdoba",
  tick1: "Ptaszek 1",
  tick2: "Ptaszek 2",
  tick3: "Ptaszek 3",
  cross: "Przekreślenie",
  box_a: "Ramka A",
  box_b: "Ramka B",
  box_c: "Ramka C",
  arrow_ab: "Strzałka A→B",
  arrow_bc: "Strzałka B→C",
};

function roleLabel(role: string): string {
  return ROLE_LABELS[role] || role.replace(/_/g, " ");
}

function verdictLabel(verdict: string): { title: string; hint: string } {
  const v = String(verdict || "").toLowerCase();
  if (v === "ship") return { title: "Można publikować", hint: "" };
  if (v === "fix") return { title: "Do poprawy", hint: "Podmień element albo złóż ponownie." };
  if (v === "kill")
    return {
      title: "Werdykt: odrzuć",
      hint: "Pusto albo nieczytelne. Złóż grafikę ponownie.",
    };
  return { title: `Werdykt: ${verdict}`, hint: "" };
}

function mutationError(err: unknown): string {
  if (!err) return "";
  if (typeof err === "string") return err;
  if (err instanceof Error) return err.message;
  const detail = (err as { detail?: unknown; message?: string })?.detail;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map(String).join("; ");
  return String((err as { message?: string })?.message || err);
}

export default function PostAtelierPanel({ item, onItem }: Props) {
  const qc = useQueryClient();
  const [note, setNote] = useState("");
  const [borrow, setBorrow] = useState(1);
  const [allow3, setAllow3] = useState(false);
  const [formatId, setFormatId] = useState("");
  const [stepOverride, setStepOverride] = useState<StepId | null>(null);
  const [swap, setSwap] = useState<{ role: string; kind: string; elementId?: string; tags?: string[] } | null>(
    null,
  );
  const metaQ = useQuery({ queryKey: ["elements-meta"], queryFn: () => api.elementsMeta() });
  const inspiration = (item.design_review as { post_inspiration?: PostInspirationBrief } | undefined)
    ?.post_inspiration;
  const plan = (item.design_review as { post_plan?: PostCompositionPlan } | undefined)?.post_plan;
  const review = (item.design_review as { post_review?: PostVerifyResult } | undefined)?.post_review;
  const slide = item.copy?.slides?.[0];
  const heading = String(slide?.heading || item.hook || "").trim();
  const caption = String(item.caption || "").trim();
  const hasPng = Boolean(
    item.slides?.some((s) => s.png_relpath) || item.render_status === "done",
  );
  const pngUrl = hasPng
    ? `/api/carousels/${item.id}/slides/1/png?t=${encodeURIComponent(item.updated_at || "")}`
    : "";

  const derivedStep: StepId = !heading ? 1 : !plan ? 2 : !hasPng ? 3 : 4;
  const step = stepOverride ?? derivedStep;

  const inspireM = useMutation({
    mutationFn: () =>
      api.carouselPostInspire(item.id, {
        note,
        borrow_intensity: borrow,
        allow_borrow_3: allow3,
      }),
    onSuccess: (res) => {
      onItem?.(res.item);
      qc.invalidateQueries({ queryKey: ["carousel", item.id] });
    },
  });
  const planM = useMutation({
    mutationFn: () =>
      api.carouselPostPlan(item.id, {
        format_id: formatId || inspiration?.suggested_format_id || "",
        inspiration,
        use_llm: false,
      }),
    onSuccess: (res) => {
      onItem?.(res.item);
      qc.invalidateQueries({ queryKey: ["carousel", item.id] });
      setStepOverride(3);
    },
  });
  const composeM = useMutation({
    mutationFn: () => api.carouselPostCompose(item.id, { verify: true }),
    onSuccess: (res) => {
      onItem?.(res.item);
      qc.invalidateQueries({ queryKey: ["carousel", item.id] });
      setStepOverride(4);
    },
  });
  const verifyM = useMutation({
    mutationFn: () => api.carouselPostVerify(item.id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["carousel", item.id] }),
  });

  const formats = metaQ.data?.formats?.formats || [];
  const slots = plan?.element_slots || [];
  const formatLabel = useMemo(() => {
    const id = plan?.format_id || formatId || inspiration?.suggested_format_id || "";
    return formats.find((f) => f.id === id)?.label || id;
  }, [formats, plan?.format_id, formatId, inspiration?.suggested_format_id]);

  const applySwap = async (el: GraphicElement) => {
    if (!swap || !plan) return;
    const next: PostCompositionPlan = {
      ...plan,
      element_slots: (plan.element_slots || []).map((s) =>
        s.role === swap.role ? { ...s, element_id: el.id, request: null } : s,
      ),
    };
    await api.carouselPostCompose(item.id, { plan: next, verify: false });
    qc.invalidateQueries({ queryKey: ["carousel", item.id] });
    setSwap(null);
    setStepOverride(4);
  };

  const steps: { id: StepId; label: string }[] = [
    { id: 1, label: "Treść" },
    { id: 2, label: "Format" },
    { id: 3, label: "Plan" },
    { id: 4, label: "Wynik" },
  ];

  const vLabel = review ? verdictLabel(review.verdict) : null;
  const lastErr =
    mutationError(inspireM.error) ||
    mutationError(planM.error) ||
    mutationError(composeM.error) ||
    mutationError(verifyM.error);

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Warsztat 1-grafika</CardTitle>
          <p className="text-xs text-muted-foreground">
            Kolejność: treść → format (inspiracja opcjonalna) → plan → grafika. Bez wrzutki to normalna
            ścieżka.
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap gap-2">
            {steps.map((s) => (
              <button
                key={s.id}
                type="button"
                className={cn(
                  "rounded-md border px-3 py-1.5 text-sm",
                  step === s.id
                    ? "border-brand bg-brand/10 font-medium text-foreground"
                    : "border-border text-muted-foreground hover:bg-muted/40",
                )}
                onClick={() => setStepOverride(s.id)}
              >
                {s.id}. {s.label}
              </button>
            ))}
          </div>

          {step === 1 ? (
            <div className="space-y-3">
              <div className="rounded-md border border-border bg-muted/20 p-3 text-sm">
                <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  Tekst na grafikę
                </div>
                <div className="mt-1 whitespace-pre-wrap">
                  {heading || <span className="text-muted-foreground">Brak heading. Wróć do Przetwarzania.</span>}
                </div>
                {caption ? (
                  <div className="mt-3 border-t border-border/60 pt-2 text-xs text-muted-foreground">
                    Caption (skrót): {caption.slice(0, 180)}
                    {caption.length > 180 ? "…" : ""}
                  </div>
                ) : null}
              </div>
              {!heading ? (
                <p className="text-sm text-amber-800 dark:text-amber-200">
                  Najpierw uzupełnij treść w zakładce Przetwarzanie (heading + caption), potem wróć tu.
                </p>
              ) : (
                <Button type="button" onClick={() => setStepOverride(2)}>
                  Idź dalej: format
                </Button>
              )}
            </div>
          ) : null}

          {step === 2 ? (
            <div className="space-y-3">
              <div className="flex flex-wrap items-center gap-2">
                <label className="text-sm font-medium" htmlFor="post-format">
                  Format
                </label>
                <select
                  id="post-format"
                  className="rounded-md border border-border bg-background px-2 py-1.5 text-sm"
                  value={formatId || inspiration?.suggested_format_id || ""}
                  onChange={(e) => setFormatId(e.target.value)}
                >
                  <option value="">Wybierz format…</option>
                  {formats.map((f) => (
                    <option key={f.id} value={f.id}>
                      {f.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="rounded-md border border-dashed border-border p-3 space-y-2">
                <div className="flex flex-wrap items-center gap-2 text-sm">
                  <span className="font-medium">Inspiracja (opcjonalna)</span>
                  <span className="rounded-full border border-border bg-muted/40 px-2 py-0.5 text-xs text-muted-foreground">
                    Bez wrzutki, house OK
                  </span>
                </div>
                <Input
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="Notatka / kąt (opcjonalnie). Upload PNG później."
                />
                <div className="flex flex-wrap items-center gap-3 text-sm">
                  <label className="flex items-center gap-2">
                    Jak blisko inspiracji (0–3)
                    <input
                      type="range"
                      min={0}
                      max={3}
                      value={borrow}
                      onChange={(e) => setBorrow(Number(e.target.value))}
                    />
                    <span className="w-4 tabular-nums">{borrow}</span>
                  </label>
                  <label className="flex items-center gap-2 text-xs text-muted-foreground">
                    <input type="checkbox" checked={allow3} onChange={(e) => setAllow3(e.target.checked)} />
                    pozwól na 3 (świadomie)
                  </label>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={inspireM.isPending}
                  onClick={() => inspireM.mutate()}
                >
                  {inspireM.isPending ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <Sparkles className="mr-2 h-4 w-4" />
                  )}
                  Odczyt inspiracji
                </Button>
                {inspiration ? (
                  <div className="text-xs text-muted-foreground">
                    Odczyt: bliskość {inspiration.borrow_intensity}
                    {inspiration.borrow_why || inspiration.content_angle
                      ? ` · ${inspiration.borrow_why || inspiration.content_angle}`
                      : ""}
                    {inspiration.drop?.length ? ` · Odrzuć: ${inspiration.drop.join(", ")}` : ""}
                  </div>
                ) : (
                  <div className="text-xs text-muted-foreground">Bez wrzutki (OK). Składamy w house.</div>
                )}
              </div>

              <div className="flex flex-wrap gap-2">
                <Button type="button" variant="outline" onClick={() => setStepOverride(1)}>
                  Wróć do treści
                </Button>
                <Button
                  type="button"
                  disabled={planM.isPending || !heading}
                  onClick={() => planM.mutate()}
                >
                  {planM.isPending ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <Wand2 className="mr-2 h-4 w-4" />
                  )}
                  Zaplanuj skład
                </Button>
              </div>
              {!heading ? (
                <p className="text-xs text-amber-800 dark:text-amber-200">Zaplanuj wymaga heading z Przetwarzania.</p>
              ) : null}
              {planM.isError ? (
                <p className="text-sm text-destructive">{mutationError(planM.error)}</p>
              ) : null}
              {inspireM.isError ? (
                <p className="text-sm text-destructive">{mutationError(inspireM.error)}</p>
              ) : null}
            </div>
          ) : null}

          {step === 3 ? (
            <div className="space-y-3">
              {plan ? (
                <div className="rounded-md border border-border p-3 text-sm space-y-2">
                  <div className="font-medium">
                    Plan · {formatLabel || plan.format_id}
                    {plan.look ? ` · wygląd ${plan.look}` : ""}
                  </div>
                  {plan.rationale ? (
                    <div className="text-xs text-muted-foreground">{plan.rationale}</div>
                  ) : null}
                  <div className="flex flex-wrap gap-2">
                    {slots.map((s) => (
                      <button
                        key={s.role}
                        type="button"
                        className={cn(
                          "rounded-full border px-2.5 py-1 text-xs",
                          s.element_id ? "border-brand/40 bg-brand/10" : "border-dashed border-amber-400",
                        )}
                        onClick={() =>
                          setSwap({
                            role: s.role,
                            kind: String(s.request?.kind || (s.element_id ? "arrow" : "icon")),
                            elementId: s.element_id || undefined,
                            tags: s.request?.tags_wanted || [],
                          })
                        }
                      >
                        {roleLabel(s.role)}
                        {s.element_id ? " · Podmień" : " · dobierz"}
                      </button>
                    ))}
                  </div>
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">
                  Brak planu. Wróć do kroku Format i kliknij Zaplanuj skład.
                </p>
              )}
              <div className="flex flex-wrap gap-2">
                <Button type="button" variant="outline" onClick={() => setStepOverride(2)}>
                  Wróć do formatu
                </Button>
                <Button
                  type="button"
                  disabled={composeM.isPending || !plan}
                  onClick={() => composeM.mutate()}
                >
                  {composeM.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                  Złóż grafikę
                </Button>
              </div>
              {composeM.isError ? (
                <p className="text-sm text-destructive">{mutationError(composeM.error)}</p>
              ) : null}
            </div>
          ) : null}

          {step === 4 ? (
            <div className="space-y-3">
              {pngUrl ? (
                <div className="overflow-hidden rounded-lg border border-border bg-[#f8f5ef]">
                  <img src={pngUrl} alt="Podgląd posta" className="mx-auto max-h-[70vh] w-auto" />
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">Brak PNG. Wróć do Planu i złóż grafikę.</p>
              )}
              {vLabel ? (
                <div className="rounded-md border border-border p-3 text-sm">
                  <div className="font-medium">{vLabel.title}</div>
                  {review?.notes ? (
                    <div className="mt-1 text-xs text-muted-foreground">{review.notes}</div>
                  ) : vLabel.hint ? (
                    <div className="mt-1 text-xs text-muted-foreground">{vLabel.hint}</div>
                  ) : null}
                </div>
              ) : null}
              <div className="flex flex-wrap gap-2">
                <Button type="button" variant="outline" onClick={() => setStepOverride(3)}>
                  Wróć do planu
                </Button>
                <Button
                  type="button"
                  disabled={composeM.isPending || !plan}
                  onClick={() => composeM.mutate()}
                >
                  {composeM.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                  Złóż ponownie
                </Button>
                <Button
                  type="button"
                  variant="secondary"
                  disabled={verifyM.isPending || !pngUrl}
                  onClick={() => verifyM.mutate()}
                >
                  {verifyM.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                  Oceń ponownie
                </Button>
              </div>
              {composeM.isError || verifyM.isError ? (
                <p className="text-sm text-destructive">{lastErr}</p>
              ) : null}
            </div>
          ) : null}
        </CardContent>
      </Card>
      <ElementSwapDrawer
        open={Boolean(swap)}
        onClose={() => setSwap(null)}
        kind={swap?.kind || "arrow"}
        currentId={swap?.elementId}
        seedTags={swap?.tags || []}
        onPick={(el) => {
          void applySwap(el);
        }}
      />
    </div>
  );
}
